import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AssociationsComponent from '../../../../app/pages/InteractiveTest/components/Associations';
import { PluginProps } from '../Plugin';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { useQueryParam } from '../../../../../hooks/useQueryParams';
import {
  useInteractiveComponentStateQuery,
  useSaveInteractiveComponentStateMutation,
} from '../../../../../generated/graphqlModels';
import { MD5 } from '../../../../../helpers/utils';
import SelectUser from '../../SelectUser';
import { Button } from '@chakra-ui/react';
import { GET_INTERACTIVE_COMPONENT_STATE } from '../../../../app/pages/InteractiveTest/components/shared/queries';
import { useIsMobile } from '../../../../../hooks/isMobile';

const Associations = (props: PluginProps) => {
  const { content, studyGroup } = props;
  const { t } = useTranslation();
  const contentData = content.data;
  const user = useContext(AuthContext);
  const debugParam = useQueryParam('debug');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    user?.pk || null,
  );
  const [showCorrect, setShowCorrect] = useState(false);
  const [canShowCorrectButton, setCanShowCorrectButton] = useState(false);

  const debug = user?.roles.includes(Role.staff) && !!debugParam;

  const studyGroupId = studyGroup.pk;

  const queryVariables = {
    userId: selectedUserId! || user?.pk!,
    componentId: contentData.unique_id,
    studyGroupId: studyGroupId!,
  };

  const isMobile = useIsMobile();

  const {
    data,
    // loading, refetch
  } = useInteractiveComponentStateQuery({
    variables: queryVariables,
    skip: user == null || user.pk == null || studyGroupId == null,
  });

  const [
    saveState,
    // { data: mutData, loading: mutLoading, error: mutError }
  ] = useSaveInteractiveComponentStateMutation({
    refetchQueries: [
      {
        query: GET_INTERACTIVE_COMPONENT_STATE,
        variables: queryVariables,
      },
    ],
  });

  const hash = useMemo(() => {
    return MD5.hash(JSON.stringify(contentData.config));
  }, [contentData.config]);

  const submit = (incorrect: number, total: number, state: any) => {
    saveState({
      variables: {
        data: {
          componentId: contentData.unique_id,
          userId: user!.pk,
          studyGroupId: studyGroupId!,
          hash,
          state,
        },
        reset: false,
      },
    });
  };

  const reset = () => {
    saveState({
      variables: {
        data: {
          componentId: contentData.unique_id,
          userId: user!.pk,
          studyGroupId: studyGroupId!,
          hash,
          state: '',
        },
        reset: true,
      },
    });
  };

  const isViewOnly = !!selectedUserId && selectedUserId !== user?.pk!;

  const handleCanShowCorrectChange = useCallback(
    (canShow: boolean) => setCanShowCorrectButton(canShow),
    [],
  );

  const ShowCorrectButton = () =>
    user?.roles.includes(Role.teacher) && canShowCorrectButton ? (
      <Button onClick={() => setShowCorrect(!showCorrect)} marginTop='30px'>
        {showCorrect ? t('plugin.hideCorrect') : t('plugin.showCorrect')}
      </Button>
    ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '10px', marginTop: '10px' }}>
      <SelectUser
        studyGroupId={studyGroupId}
        studentId={selectedUserId}
        setStudentId={setSelectedUserId}
      />
      <div
        style={
          isMobile
            ? {
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
              }
            : {
                textAlign: 'center',
              }
        }
      >
        <AssociationsComponent
          key={`${contentData.id}-${selectedUserId ?? user?.pk ?? 'current'}`}
          associations={contentData.config}
          dndPosition={isMobile ? 'bottom' : contentData.dnd_position}
          debug={debug}
          showCorrect={showCorrect}
          submit={submit}
          reset={reset}
          initialState={data?.getInteractiveContentState?.state}
          isViewOnly={isViewOnly}
          onCanShowCorrectChange={handleCanShowCorrectChange}
        />
        <ShowCorrectButton />
      </div>
    </div>
  );
};

export default Associations;
